/* eslint-disable no-fallthrough */

/**
 * JS Implementation of MurmurHash3 (r136) (as of May 20, 2011)
 * @see http://github.com/garycourt/murmurhash-js
 *
 * We modified the version from github:
 * - JSON.stringify if the key is an object (note that the key argument is not type checked in jsx)
 * - returning a string, so that do not have to alter PropTypes of dozens of components.
 *
 * We could use a wrapper fn around the npm package. But the alog/implementation is unlikely
 * to change in the future, so we have to care about one dependency less...
 *
 * @param string key
 * @param number seed positive integer only
 * @return string 32-bit positive integer hash
 */
export function murmurhash(key: string | Record<string, unknown>, seed = 0): string {
  // using local underscore version b/c I don't have a better name
  // eslint-disable-next-line no-underscore-dangle
  const _key = typeof key === 'object' ? JSON.stringify(key) : key;
  const remainder = _key.length & 3;
  const bytes = _key.length - remainder;
  let h1 = seed;
  const c1 = 0xcc9e2d51;
  const c2 = 0x1b873593;
  let h1b;
  let k1;
  let i;

  i = 0;

  while (i < bytes) {
    k1 =
      (_key.charCodeAt(i) & 0xff)
      | ((_key.charCodeAt(++i) & 0xff) << 8)
      | ((_key.charCodeAt(++i) & 0xff) << 16)
      | ((_key.charCodeAt(++i) & 0xff) << 24);
    ++i;

    k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
    k1 = (k1 << 15) | (k1 >>> 17);
    k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;

    h1 ^= k1;
    h1 = (h1 << 13) | (h1 >>> 19);
    h1b = ((h1 & 0xffff) * 5 + ((((h1 >>> 16) * 5) & 0xffff) << 16)) & 0xffffffff;
    h1 = (h1b & 0xffff) + 0x6b64 + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16);
  }

  k1 = 0;

  switch (remainder) {
    case 3:
      k1 ^= (_key.charCodeAt(i + 2) & 0xff) << 16;
    case 2:
      k1 ^= (_key.charCodeAt(i + 1) & 0xff) << 8;
    case 1:
      k1 ^= _key.charCodeAt(i) & 0xff;

      k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
      k1 = (k1 << 15) | (k1 >>> 17);
      k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
      h1 ^= k1;
  }

  h1 ^= _key.length;

  h1 ^= h1 >>> 16;
  h1 = ((h1 & 0xffff) * 0x85ebca6b + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff;
  h1 ^= h1 >>> 13;
  h1 = ((h1 & 0xffff) * 0xc2b2ae35 + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16)) & 0xffffffff;
  h1 ^= h1 >>> 16;

  return (h1 >>> 0).toString();
}

/* eslint-enable no-fallthrough */
